import * as React from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import Popover from 'react-bootstrap/Popover';
import ReactQuill from 'react-quill-v2.0';
import shortid from 'shortid';
import axios from 'axios';
import AxiosConfig from '../../../../utilities/AxiosConfig';
import { useDebounce } from '../../../../utilities/CustomHooks';

interface IProps {
    /** Tooltip Header */
    header?: string | any;
    /** Name of the form in the database, Will trigger call to API */
    name?: string | number;
    /** Custom HTML String to show as value */
    value?: string;
    /** Icon from material-icons library to show */
    icon?: string;
    /** Custom class for icon */
    iconClassName?: string;
    /** Where to place the tooltip when rendering */
    placement?: "top" | "bottom" | "left" | "right";
    placeholder?: string;
    /** Shows tooltip on hover */
    showOnHover?: boolean;
    /** Footer of the tooltip. */
    footer?: string | any;
}

const Tooltip: React.FC<IProps> = (props: IProps) => {
    const { header, name, showOnHover } = props;
    const [loading, setLoading] = React.useState(false);
    const [show, setShow] = React.useState(false);
    const [value, setValue] = React.useState("")
    const hasRetrievedValue = React.useRef(false);
    //? Hovering Logic
    const isClicked: any = React.useRef(false);
    const [hoveringStatus, setHoveringStatus] = React.useState(false);
    const delay: any = React.useRef(500);
    const isHovering = useDebounce(hoveringStatus, delay.current);

    const toggleShow = (e: any, shouldShow: any = null) => {
        e?.preventDefault();
        let shouldOpen = shouldShow === null ? !show : shouldShow;
        setShow(shouldOpen);
        if (shouldOpen && !hasRetrievedValue.current) {
            if (name && !value) {
                setLoading(true);
                const endingRoute: string = typeof name === 'number' ? 'GetDynamicFormDefinitionById' : 'GetDynamicFormDefinitionByName';
                axios.get(process.env.REACT_APP_API_URL + `/api/company/${endingRoute}/${name}`, AxiosConfig.configValue("min"))
                    .then((res: any) => {
                        //console.log("Info Tooltip", res);
                        hasRetrievedValue.current = true;
                        if (res.data[0]) {
                            let fields: any = JSON.parse(res.data[0].FieldsSchema);
                            const usePortalSettings = res.data[0].CustomFormTypeId === 4;
                            if (usePortalSettings) {
                                let configFields = fields.find((field: any) => field.Type === "Config").CustomValues?.map((value: any) => JSON.parse(value));
                                let staticText = configFields.find((field: any) => field.type === "statictext");
                                if (staticText) {
                                    setValue(staticText.defaultValue);
                                } else {
                                    setValue(props.placeholder || "");
                                }
                            } else {
                                let staticText: any = fields.find((field: any) => field.Type === "StaticText");
                                let parsed: any = typeof staticText.CustomValues === "string" ? JSON.parse(staticText.CustomValues) : staticText.CustomValues;
                                setValue(parsed[0] || "");
                            }
                        } else {
                            setValue(props.placeholder || "No Data...")
                        }
                    })
                    .catch((err: any) => {
                        //console.log("Info Tooltip", err);
                        setValue(props.placeholder || "No Data...");
                    })
                    .finally(() => {
                        setLoading(false);
                    })
            }
            else if (!name && props.value) {
                setValue(props.value || "");
            }
        }
    }

    const handleMouseEnter = () => {
        // delay.current = 100;
        setHoveringStatus(true);
    }

    const handleMouseLeave = () => {
        // delay.current = 500;
        setHoveringStatus(false);
    }

    const handleClick = (e: any) => {
        e?.preventDefault();
        const isClickedNow = !isClicked.current;
        isClicked.current = isClickedNow;
        toggleShow(null, isClickedNow);
    }

    React.useEffect(() => {
        // delay.current = 500;
        if (isClicked.current === false) {
            if (isHovering && showOnHover && !show) {
                toggleShow(null, true);
            } else if (!isHovering && showOnHover && show) {
                toggleShow(null, false);
            }
        }
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [isHovering])

    return (
        <OverlayTrigger
            trigger="click"
            placement={props.placement || "top"}
            show={show}
            overlay={
                <Popover
                    {...props}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    placement={props.placement}
                    style={{ maxWidth: 350 }}
                    id={`info-tooltip-${shortid.generate()}`}
                >
                    {
                        loading
                            ?
                            <div className="center d-flex flex-column">
                                <div className="spinner-border text-info" role="status" />
                                <span className="sr-only">Loading...</span>
                            </div>
                            :
                            <React.Fragment>
                                {typeof props.header === "string" ? <Popover.Title as="h3">{header}</Popover.Title> : header()}
                                <Popover.Content>
                                    <ReactQuill
                                        readOnly={true}
                                        value={value || ""}
                                    />
                                </Popover.Content>
                                {props.footer && <div className="px-1">
                                    {typeof props.footer === "string"
                                        ? <span className="text-secondary text-align-center">{props.footer}</span>
                                        : props.footer()}
                                </div>}
                            </React.Fragment>
                    }
                </Popover>
            }
        >
            <i
                className={`material-icons rounded-circle cursor-on-hover ${props.iconClassName}`}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {props.icon}
            </i>
        </OverlayTrigger>
    )
}

Tooltip.defaultProps = {
    header: "",
    iconClassName: "bg-white text-info",
    icon: "info",
    name: "",
    value: "",
    placement: "right",
    placeholder: "",
    showOnHover: false,
    footer: ""
}

export default Tooltip;